import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CheckCircleFilled, CopyOutlined, ExportOutlined } from '@ant-design/icons';
import { Button, Steps } from 'antd';

import { message } from '@eduzz/ui-antd-theme';

import CardButton from '@/components/CardButton';
import { Confetti } from '@/components/Confetti';
import { AlmostOnTimeWatchIcon } from '@/components/form/uploader/AlmostOnTimeWatchIcon';
import Header from '@/components/globals/Header';
import CircleAdd from '@/components/icons/CircleAdd';
import Edit from '@/components/icons/Edit';
import Palette from '@/components/icons/Palette';
import Whatsapp from '@/components/icons/Whatsapp';
import { MYEDUZZ_FRONT_URL } from '@/env';
import { cn } from '@/lib/cn';
import { redirectTo } from '@/lib/postMessage';
import { trpc } from '@/lib/trpc';
import { initialState as aiInitialState, aiProductExampleStore } from '@/stores/AIProductExampleStore';
import useProductStore, { initialState as productInitialState, productStore } from '@/stores/productStore';
import { stepStore } from '@/stores/steps';

export default function ProductCompletion() {
  const navigate = useNavigate();
  const { id } = useParams();
  const linksWereSent = useProductStore(d => d.linksWereSent);
  const [data] = trpc.product.getProductById.useSuspenseQuery({ id: id || '' });

  const checkoutUrl = useMemo(() => {
    const checkoutSunData = data!.linkedServices?.find(ls => ls.name === 'checkoutSun');
    return checkoutSunData?.data.url || '';
  }, [data]);

  useEffect(() => {
    return () => {
      productStore.getState().setFields(productInitialState);
      stepStore.getState().clean();
      aiProductExampleStore.getState().setFields(aiInitialState);
    };
  }, []);

  const [nextSteps, setNextSteps] = useState<
    {
      title: string;
      completed?: boolean;
      action?: {
        label: string;
        url: string;
      };
      description?: string;
      transparent?: boolean;
    }[]
  >([
    {
      title: 'Cadastrar produto',
      completed: true
    }
  ]);

  const hasNextSteps = useMemo(() => {
    return nextSteps.some(step => !step.completed);
  }, [nextSteps]);

  const copyToClipboard = (link: string) => {
    try {
      navigator.clipboard.writeText(link);
      message.success('Link copiado para a área de transferência!');
    } catch (err) {
      message.error('Seu navegador não suporta copiar para a área de transferência.');
    }
  };

  useEffect(() => {
    if (data!.deliverables.length > 0) {
      return;
    }

    const myEduzzData = data!.linkedServices?.find(item => item.name === 'myEduzz');

    setNextSteps(() => [
      {
        title: 'Cadastrar dados do produto',
        completed: true
      },
      {
        title: 'Enviar arquivos para entrega',
        description: 'Você ainda precisa enviar os arquivos que serão entregues aos seus clientes após a compra.',
        action: {
          label: 'Enviar arquivos',
          url: myEduzzData?.data.configUrl || `${MYEDUZZ_FRONT_URL}/producer/product/myproducts`
        },
        completed: false
      }
    ]);
  }, [data, navigate]);

  return (
    <div className='w-full'>
      <Header />
      <div className='bg-surface-default flex flex-col gap-6 self-center'>
        {!data!.draft && (
          <Confetti>
            <div className='flex flex-col items-center gap-8 bg-lime-50 p-4 pt-14 pb-15 dark:bg-lime-800/20'>
              <div className='flex flex-col items-center lg:w-2/6'>
                <CheckCircleFilled className='mb-4 text-7xl text-lime-600!' />
                <span className='text-primary dark:text-content-title text-center text-2xl font-semibold'>
                  Incrível! Seu produto foi criado e já pode ser vendido. 🎉
                </span>
              </div>

              <div className='flex w-full flex-col gap-2 lg:w-2/6'>
                <span className='text-content-body text-center text-xs'>
                  Seu produto passará por uma etapa de moderação, mas você já pode começar a vender com o link abaixo!
                </span>

                <div className='bg-surface-default white border-outline-default flex w-full flex-col gap-3 rounded-lg border px-4 py-3'>
                  <span className='text-base font-semibold'>Link para vender seu produto</span>
                  <div className='flex flex-col gap-2'>
                    <div className='text-content-body bg-surface-disabled border-outline-disabled w-full truncate rounded-lg border p-3 text-sm'>
                      {checkoutUrl}
                    </div>

                    <div className='flex gap-2'>
                      <Button
                        id='copy-link'
                        className='override-button-sm! flex-1!'
                        onClick={() => copyToClipboard(checkoutUrl)}
                      >
                        <CopyOutlined />
                        Copiar link
                      </Button>
                      <Button
                        id='open-link'
                        className='override-button-sm! flex-1!'
                        onClick={() => window.open(checkoutUrl, '_blank')}
                      >
                        <ExportOutlined />
                        Abrir link
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Confetti>
        )}

        {data!.draft && (
          <div className='bg-surface-disabled flex flex-col items-center gap-4 px-4 py-14'>
            <div className='flex flex-col items-center gap-4 md:w-3/6 lg:w-2/6'>
              <AlmostOnTimeWatchIcon />
              <span className='text-center text-2xl font-semibold text-orange-400'>Estamos quase lá...</span>
            </div>

            <div className='flex flex-col items-center md:w-3/6 lg:w-4/10'>
              <span className='text-center text-base'>
                Seu produto foi salvo como rascunho. Quando o arquivo estiver pronto, adicione-o e comece a vender.
              </span>
            </div>
          </div>
        )}

        <div className='flex flex-col gap-8 self-center lg:max-w-5/6 lg:flex-row lg:px-15'>
          {hasNextSteps && (
            <div className='lg:bg-surface-subtle flex-1 px-4 py-0 pt-4 md:p-4 lg:rounded-lg'>
              <div className='pb-6'>
                <span className='text-content-title text-xl font-semibold'>Próximos passos</span>
              </div>
              <Steps
                direction='vertical'
                current={nextSteps.findIndex(step => !step.completed)}
                items={nextSteps.map(step => {
                  const transparentClass = step.transparent ? 'opacity-30' : '';

                  let icon = <CheckCircleFilled className='text-2xl text-lime-600!' />;

                  if (!step.completed) {
                    icon = (
                      <div className='flex w-full items-center justify-center'>
                        <span className={cn('border-content-title h-6 w-6 rounded-full border', transparentClass)} />
                      </div>
                    );
                  }

                  return {
                    title: <div className={cn('text-content-title font-semibold', transparentClass)}>{step.title}</div>,
                    description: (
                      <div className={cn('flex flex-col gap-2 pb-5 md:gap-4', transparentClass)}>
                        <span className='text-content-body text-sm'>{step.description}</span>

                        {step.action && (
                          <Button
                            id='btn-bank-data'
                            type='primary'
                            onClick={() => {
                              redirectTo(step.action!.url, 'btn-bank-data');
                            }}
                            className='font-base! border-outline-default! h-fit! w-fit rounded-lg! border! border-solid! px-3.5! py-1.25! text-sm!'
                            size='middle'
                          >
                            {step.action.label}
                          </Button>
                        )}
                      </div>
                    ),

                    icon
                  };
                })}
              />
            </div>
          )}

          <div className='md:bg-surface-subtle bg-surface-disabled flex flex-1 flex-col gap-3 px-4 py-8 md:rounded-lg'>
            <div className='flex flex-col gap-3'>
              <span className='text-content-title pb-1 text-xl font-semibold'>Pode te interessar</span>

              <CardButton
                id='customize-checkout-action'
                onClick={() => redirectTo('https://adminsun.eduzz.com', 'customize-checkout-action')}
                icon={Palette}
                title='Personalizar checkout'
                description={
                  <>
                    Customize o checkout, adicione <span className='font-semibold'>banners, timers</span> ou configure
                    as <span className='font-semibold'>ferramentas de conversão.</span>
                  </>
                }
              />

              <CardButton
                id='edit-product-details-action'
                onClick={() =>
                  redirectTo(`${MYEDUZZ_FRONT_URL}/producer/product/myproducts`, 'edit-product-details-action')
                }
                icon={Edit}
                title='Editar detalhes do produto'
                description={
                  <>
                    Gerencie todas as informações do seu produto. Atualize título, descrição e preço, crie{' '}
                    <span className='font-semibold'>novas ofertas</span> e{' '}
                    <span className='font-semibold'>variações</span>, e adicione{' '}
                    <span className='font-semibold'>parceiros</span> ou <span className='font-semibold'>afiliados</span>
                    .
                  </>
                }
              />

              <CardButton
                id='create-new-product-action'
                onClick={() => navigate('/product/create')}
                icon={CircleAdd}
                title='Adicionar um novo produto'
              />
            </div>
          </div>
        </div>

        <div className='mb-8 flex w-full flex-col gap-4 p-4 lg:w-2/6 lg:self-center'>
          {!data!.draft && linksWereSent && (
            <div className='flex gap-3 rounded-lg border border-lime-200 bg-lime-50 px-4 py-3 dark:border-lime-500/60 dark:bg-lime-800/20'>
              <Whatsapp className='h-5 w-5 shrink-0 align-bottom' />
              <div className='text-xs lg:text-center'>
                Enviamos uma mensagem com esses links para você salvar e acessar quando quiser.
              </div>
            </div>
          )}

          <Button
            id='btn-follow-eduzz'
            className='override-button-base! w-full'
            onClick={() => redirectTo(`${MYEDUZZ_FRONT_URL}/producer/dashboard`, 'btn-follow-eduzz')}
          >
            Continuar para a Eduzz
          </Button>
        </div>
      </div>
    </div>
  );
}
